import React, { useState, useEffect, useRef } from "react";
import {
  ContentLayout,
  Container,
  Header,
  Box,
  Spinner,
  Alert,
  SpaceBetween,
  Button,
} from "@cloudscape-design/components";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { useAlert } from "../components/Alert";
import { useInteractionType } from "../hooks/InteractionTypeContext";
import { getQuickSightEmbedUrl } from "../api/api";
import * as QuickSightEmbedding from "amazon-quicksight-embedding-sdk";

const Insights = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { showError } = useAlert();
  const { t } = useTranslation();
  const { interactionType } = useInteractionType();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  
  const containerRef = useRef(null);
  const dashboardWrapperRef = useRef(null);

  const toggleFullscreen = () => {
    if (!dashboardWrapperRef.current) return;
    
    if (!document.fullscreenElement) {
      dashboardWrapperRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error(`Error al intentar mostrar pantalla completa: ${err.message}`);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch(err => {
        console.error(`Error al salir de pantalla completa: ${err.message}`);
      });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenActive = !!document.fullscreenElement;
      setIsFullscreen(fullscreenActive);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (dashboard) {
      const timeoutId = setTimeout(() => {
        dashboard.resize();
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isFullscreen, dashboard]);


  const fetcher = async () => {
    // Leer directamente de localStorage para obtener el valor más reciente
    const storedType = localStorage.getItem('eva_interaction_type') || 'text';
    const validType = (storedType === 'audio' || storedType === 'text') 
      ? storedType 
      : 'text';
    
    const response = await getQuickSightEmbedUrl(validType);
    if (!response || !response.embeddedUrl) {
      throw new Error("Failed to get embedded URL");
    }
    return response;
  };

  // useSWR hook similar a Home.js
  const { data, error: swrError, isLoading } = useSWR(
    ['dashboard-embed', interactionType],
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    }
  );

  // useEffect para actualizar dashboard cuando cambien los datos
  useEffect(() => {
    const loadDashboard = async () => {
      if (!data?.embeddedUrl || !containerRef.current) {
        return;
      }
      
      try {
        setError(null);
        
        // Limpiar contenedor antes de cargar nuevo dashboard
        containerRef.current.innerHTML = '';
        
        // Crear nueva instancia del dashboard con la nueva URL
        const embeddingContext = await QuickSightEmbedding.createEmbeddingContext();
        const dashboardInstance = embeddingContext.embedDashboard({
          url: data.embeddedUrl,
          container: containerRef.current,
          height: "100%",
          width: "100%", 
          resizeHeightOnSizeChangedEvent: true
        });
        
        setDashboard(dashboardInstance);
      } catch (error) {
        console.error("Error al cargar el dashboard:", error);
        setError(error.message);
        showError(t("dashboard.loadError"));
      }
    };

    loadDashboard();
  }, [data, showError, t]);

  // Manejar errores de SWR
  useEffect(() => {
    if (swrError) {
      setError(swrError.message);
      showError(t("dashboard.loadError"));
    }
  }, [swrError, showError, t]);

  // Sincronizar loading state
  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t("dashboard.description")}
          actions={
            <Button 
              iconName={isFullscreen ? "view-normal" : "expand"} 
              onClick={toggleFullscreen}
              disabled={loading || !!error}
            >
              {isFullscreen ? t("dashboard.exitFullscreen") : t("dashboard.fullscreen")}
            </Button>
          }
        >
          {t("dashboard.title")}
        </Header>
      }
    >
      {error && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Container>
        <SpaceBetween size="l">
          {loading && (
            <Box textAlign="center" padding={{ bottom: "l" }}>
              <Spinner size="large" />
              <Box padding={{ top: "s" }} color="text-status-info">
                {t("dashboard.loading")}
              </Box>
            </Box>
          )}
          <div 
            ref={dashboardWrapperRef}
            style={{
              width: "100%",
              height: isFullscreen ? "100vh" : "auto",
              overflow: "auto",
            }}
          >
            <Box padding={isFullscreen ? "none" : "s"}>
              <div
                ref={containerRef}
                id="quicksight-container"
                style={{
                  height: isFullscreen ? "calc(100vh - 20px)" : "700px",
                  width: "100%",
                  maxWidth: "100%",
                  margin: 0,
                  padding: 0
                }}
              ></div>
            </Box>
          </div>
        </SpaceBetween>
      </Container>
    </ContentLayout>
  );
};

export default Insights;